import { memo, useCallback, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { graphql } from '@apollo/client/react/hoc';
import { stringify } from '@stratumn/canonicaljson';
import to from 'await-to-js';
import gql from 'graphql-tag';
import compose from 'lodash.flowright';
import isequal from 'lodash.isequal';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router';

import { useLastBatchToast } from '@/components/batchAction/batchProgressToast';
import { notify } from '@/components/toast';
import OverviewTable from '@/components/ui/table';
import { JsonEditor } from '@/components/ui/utils/jsonEditor/jsonEditor';
import { env } from '@/constant/env';
import { BUFFER } from '@/constant/pagination';
import { FAST_WORKFLOW_COUNT_QUERY } from '@/gql/queries/fast-workflow-overview.gql';
import {
  TYPE_UPDATE_WORKFLOW_CONFIG_PAYLOAD,
  TYPE_WORKFLOW,
  TYPE_WORKFLOW_CONFIG
} from '@/gql/types';
import { useArchiveTraceStates } from '@/hooks/useArchiveTraceStates';
import { isTerminalBatchJobStatus, useBatchJob } from '@/hooks/useBatchJob';
import { useUser } from '@/hooks/useUser';
import { deepGet } from '@/utils';
import { buildWorkflowContext, WorkflowContext } from '@/utils/workflowContext';
import { useWorkflowOverviewContext } from './context.workflowOverview';
import { defaultDisplayconfig } from './static/defaultDisplayConfig';
import {
  findGroupTraceStatesByIndex,
  getFiltersFromUserDisplayConfig,
  getGroupFromUserDisplayConfig,
  getSortFromUserDisplayConfig,
  saveUserDisplayConfig
} from './utils';

const WorkflowOverviewTableImpl = memo(props => {
  const {
    workflow,
    updateOverviewConfigMutation,
    handleOnLoadMore,
    refetchTraces,
    exportSetup
  } = props;

  const {
    selectedTraceIds,
    setSelectedTraceIds,
    showNewTracesTray,
    showTracesUpdateTray,
    toggleNewTracesTray,
    toggleUpdateTracesTray,
    toggleArchiveDisplayTraces,
    archiveDisplayTraces,
    userDisplayConfig,
    setUserDisplayConfig
  } = useWorkflowOverviewContext();

  // Tracks the workflow's most recent batch purely to know whether one is still
  // running — the history page is reached from the navbar, not from here.
  const lastBatch = useLastBatchToast(String(workflow.rowId));
  const { job: lastBatchJob } = useBatchJob(lastBatch?.jobId ?? null);
  const lastBatchStatus = useMemo(() => {
    if (!lastBatch) {
      return null;
    }
    if (!(lastBatchJob && isTerminalBatchJobStatus(lastBatchJob.status))) {
      return 'running';
    }
    return lastBatchJob.failed > 0 ? 'failed' : 'done';
  }, [lastBatch, lastBatchJob]);

  // Phase 1: no parallel batches. Disable the "Update" entry point (with an
  // explanatory tooltip) while THIS workflow has a running batch. Keyed off
  // `lastBatchStatus`, which is driven by useBatchJob's live poll — so it
  // auto-clears the instant the job goes terminal (no stuck-disabled button),
  // survives reload (persisted pointer) and toast dismiss (close never clears
  // the entry). Only when the batch feature is on.
  const updateDisabledReason =
    env.FEATURE_FLAGS.USE_BATCH_ACTION && lastBatchStatus === 'running'
      ? "A batch action is already running on this workflow. You can't start another until it finishes."
      : null;

  const { config = {} } = workflow;
  const { isPrivileged } = useUser();
  const archiveTraceStates = useArchiveTraceStates({
    onSuccess: refetchTraces
  });
  const canArchive = isPrivileged || workflow.canUpdate;

  const updateRowsArchiveStatus = useCallback(
    archived => {
      if (!selectedTraceIds.length) return;
      archiveTraceStates(selectedTraceIds, archived);
      setSelectedTraceIds([]);
    },
    [selectedTraceIds, archiveTraceStates, setSelectedTraceIds]
  );

  const { rowId: configRowId, overview: configOverview } = config;
  const displayConfig = configOverview || defaultDisplayconfig;

  const location = useLocation();

  // Define state
  const [showTableConfigEditor, setShowTableConfigEditor] = useState(false);
  const toggleShowTableConfigEditor = useCallback(
    () => setShowTableConfigEditor(!showTableConfigEditor),
    [showTableConfigEditor, setShowTableConfigEditor]
  );
  const [refetching, setRefetching] = useState(false);

  // build the workflow context passed to the widgets framework
  const workflowContext = useMemo(
    () => buildWorkflowContext(workflow),
    [workflow]
  );

  const onUpdateUserDisplayConfig = useCallback(
    newUserDisplayConfig => {
      // get sort, filter and group by configs

      const newFilter = getFiltersFromUserDisplayConfig(newUserDisplayConfig);
      const newOrderBy = getSortFromUserDisplayConfig(newUserDisplayConfig);
      const newGroupBy = getGroupFromUserDisplayConfig(newUserDisplayConfig);

      const previousFilter = getFiltersFromUserDisplayConfig(userDisplayConfig);
      const previousOrderBy = getSortFromUserDisplayConfig(userDisplayConfig);
      const previousGroupBy = getGroupFromUserDisplayConfig(userDisplayConfig);

      const isFilterByEqual = isequal(previousFilter, newFilter);
      const isOrderByEqual = isequal(previousOrderBy, newOrderBy);
      const isGroupByEqual = isequal(previousGroupBy, newGroupBy);

      saveUserDisplayConfig(workflow.rowId, newUserDisplayConfig);

      if (!(isFilterByEqual && isOrderByEqual && isGroupByEqual)) {
        ReactDOM.unstable_batchedUpdates(() => {
          setUserDisplayConfig(newUserDisplayConfig);
          setRefetching(true);
        });

        // Refetch traces with the new variables
        return refetchTraces({
          workflowId: workflow.rowId,
          filter: newFilter,
          orderBy: newOrderBy,
          groupBy: newGroupBy
        }).then(() => {
          setRefetching(false);
        });
      }
      return setUserDisplayConfig(newUserDisplayConfig);
    },
    [userDisplayConfig, setUserDisplayConfig, setRefetching, refetchTraces]
  );

  const handleTableConfigUpdate = useCallback(
    async newOverviewConfigStr => {
      const { rowId: workflowRowId, config: { rowId: workflowConfigId } = {} } =
        workflow;
      const newOverviewConfig = newOverviewConfigStr
        ? JSON.parse(newOverviewConfigStr)
        : null;

      const promise = updateOverviewConfigMutation({
        variables: {
          workflowConfigId,
          newOverviewConfig
        },
        optimisticResponse: {
          updateWorkflowConfigByRowId: {
            workflow: {
              rowId: workflowRowId,
              config: {
                rowId: workflowConfigId,
                overview: newOverviewConfig,
                __typename: TYPE_WORKFLOW_CONFIG
              },
              __typename: TYPE_WORKFLOW
            },
            __typename: TYPE_UPDATE_WORKFLOW_CONFIG_PAYLOAD
          }
        }
      });

      notify.promise(promise, {
        loading: '🛠️ Updating Overview table configuration...',
        success: 'The Overview table config was correctly updated',
        error: 'Something went wrong during the Overview table config update...'
      });

      await to(promise);
    },
    [workflow]
  );

  // Live count of traces matching the currently active overview filter — used
  // by the "Update" button label (e.g. "Update (3 selected / 28 matching)")
  // when a filter is active. Skipped when no filter is set so an unfiltered
  // overview does not run a superfluous count query. The Apollo cache key is
  // identical to the one in workflowOverviewActions (same variables), so this
  // deduplicates with the tray's own count query.
  const activeFilter = useMemo(
    () => getFiltersFromUserDisplayConfig(userDisplayConfig),
    [userDisplayConfig]
  );
  const hasActiveFilter = !!activeFilter;
  const { data: matchingCountData } = useQuery(FAST_WORKFLOW_COUNT_QUERY, {
    variables: {
      workflowId: workflow.rowId,
      filter: activeFilter,
      archived: archiveDisplayTraces
    },
    skip: !hasActiveFilter,
    fetchPolicy: 'cache-and-network'
  });
  const matchingCount =
    matchingCountData?.workflowCount?.fastTraceStatesCount ?? 0;

  const { stateData, hasNextPage } = useMemo(() => {
    const selectedGroupIndex = deepGet(userDisplayConfig, 'groupBy.selected');
    const groupBy = getGroupFromUserDisplayConfig(userDisplayConfig);
    const { groupedTraceStates } = workflow;
    const traceStates = findGroupTraceStatesByIndex(
      groupedTraceStates,
      selectedGroupIndex
    );

    const data = groupBy
      ? groupedTraceStates.nodes.filter(g => g.traceStates.nodes.length)
      : traceStates.nodes;

    return { hasNextPage: traceStates.pageInfo.hasNextPage, stateData: data };
  }, [workflow.groupedTraceStates, userDisplayConfig?.groupBy]);

  /**
   * Table update configuration
   * @type {import('@/components/ui/table/table.type').TableUpdate}
   */
  const tableUpdate = useMemo(
    () => ({
      loading: refetching,
      disableClientSideSortFilter: true,
      onClickConfig:
        configRowId !== undefined && isPrivileged
          ? toggleShowTableConfigEditor
          : null,
      configClicked: showTableConfigEditor,
      onClickNew: toggleNewTracesTray,
      newClicked: showNewTracesTray,
      onClickArchive: () => {
        setSelectedTraceIds([]);
        toggleArchiveDisplayTraces();
      },
      archiveClicked: archiveDisplayTraces,
      updateRowsArchiveStatus: canArchive ? updateRowsArchiveStatus : null,
      onClickUpdate: env.FEATURE_FLAGS.USE_BATCH_UPDATE
        ? toggleUpdateTracesTray
        : null,
      updateClicked: env.FEATURE_FLAGS.USE_BATCH_UPDATE
        ? showTracesUpdateTray
        : false,
      // Filter-mode signal for the "Update (X selected / N matching)" label.
      // Only meaningful when USE_BATCH_ACTION is on; otherwise the label falls
      // back to selection-only. Kept flag-scoped so a disabled batch action
      // does not advertise a filter-mode entry point the user cannot use.
      matchingCount:
        env.FEATURE_FLAGS.USE_BATCH_ACTION && hasActiveFilter
          ? matchingCount
          : 0,
      // Phase 1: block launching a second parallel batch (disabled + tooltip).
      updateDisabledReason,
      setSelectedRows: setSelectedTraceIds,
      userDisplay: {
        config: userDisplayConfig,
        onUpdate: onUpdateUserDisplayConfig
      },
      pagination: {
        buffer: BUFFER,
        hasNextPage,
        onLoadMore: handleOnLoadMore
      },
      group: {
        indexPath: 'index',
        rowsPath: 'traceStates.nodes[]',
        rowCountPath: 'traceStates.totalCount'
      },
      exportSetup
    }),
    [
      refetching,
      showTableConfigEditor,
      toggleNewTracesTray,
      showNewTracesTray,
      toggleUpdateTracesTray,
      showTracesUpdateTray,
      setSelectedTraceIds,
      toggleArchiveDisplayTraces,
      updateRowsArchiveStatus,
      userDisplayConfig,
      onUpdateUserDisplayConfig,
      hasNextPage,
      handleOnLoadMore,
      exportSetup,
      hasActiveFilter,
      matchingCount,
      workflow.rowId,
      updateDisabledReason
    ]
  );

  return (
    <>
      <div className="flex size-full overflow-hidden p-1 lg:p-3">
        <WorkflowContext.Provider value={workflowContext}>
          <OverviewTable
            config={displayConfig}
            data={stateData}
            headerConfig={{
              newLabel: 'New Trace',
              fastOverview: location.pathname.match(/\/workflow\/\d*/g) != null
            }}
            id="workflow-overview-table"
            update={tableUpdate}
          />
        </WorkflowContext.Provider>
      </div>
      {showTableConfigEditor && (
        <JsonEditor
          jsonString={stringify(displayConfig, null, 2)}
          onClose={toggleShowTableConfigEditor}
          onSubmit={handleTableConfigUpdate}
          title="Table configuration"
        />
      )}
    </>
  );
});

WorkflowOverviewTableImpl.propTypes = {
  user: PropTypes.object.isRequired,
  workflow: PropTypes.object.isRequired,
  updateOverviewConfigMutation: PropTypes.func.isRequired,
  handleOnLoadMore: PropTypes.func.isRequired,
  refetchTraces: PropTypes.func.isRequired,
  exportSetup: PropTypes.shape({
    getConfirmationSetup: PropTypes.func.isRequired,
    generateExportPayload: PropTypes.func.isRequired
  })
};

const UPDATE_OVERVIEW_CONFIG_MUTATION = gql`
  mutation updateOverviewConfigMutation(
    $workflowConfigId: BigInt!
    $newOverviewConfig: JSON
  ) {
    updateWorkflowConfigByRowId(
      input: {
        rowId: $workflowConfigId
        patch: { overview: $newOverviewConfig }
      }
    ) {
      workflow {
        rowId
        config {
          rowId
          overview
        }
      }
    }
  }
`;

export const WorkflowOverviewTable = compose(
  graphql(UPDATE_OVERVIEW_CONFIG_MUTATION, {
    name: 'updateOverviewConfigMutation'
  })
)(WorkflowOverviewTableImpl);
